/**
 * Analytics utility wrapper for Vercel Analytics
 * 
 * Provides reliable event tracking with delay support and sendBeacon fallback
 * to ensure events are sent before navigation.
 */

import { track } from "@vercel/analytics";
import Logger from "@/lib/utils/logger";

// Track pending events for beforeunload flushing
const pendingEvents: Array<{ name: string; properties?: Record<string, unknown> }> = [];

/**
 * Track an event with a delay to ensure it's sent before navigation.
 * Returns a Promise that resolves after a delay, allowing await before navigation.
 * 
 * @param name - Event name
 * @param properties - Event properties (user_id should be included when available)
 * @param delayMs - Delay in milliseconds before resolving (default: 150ms)
 * @returns Promise that resolves after the delay
 */
export async function trackEventWithDelay(
  name: string,
  properties?: Record<string, unknown>,
  delayMs: number = 150
): Promise<void> {
  try {
    // Call the original track function immediately
    track(name, properties as Record<string, string | number | boolean | null>);
    
    // Log in development to validate events are being sent
    if (process.env.NODE_ENV === "development") {
      Logger.debug(`[Analytics] Tracked event: ${name}`, properties || {});
    }
    
    // Store for beforeunload flushing (safety net)
    pendingEvents.push({ name, properties });
    
    // Return a promise that resolves after delay to give script time to send
    return new Promise((resolve) => {
      setTimeout(() => {
        if (process.env.NODE_ENV === "development") {
          Logger.debug(`[Analytics] Event delay completed for: ${name}`);
        }
        resolve();
      }, delayMs);
    });
  } catch (error) {
    // Don't block user flow if tracking fails
    Logger.error(`[Analytics] Failed to track event: ${name}`, error);
    // Resolve immediately on error so navigation isn't blocked
    return Promise.resolve();
  }
}

/**
 * Track an event immediately (no delay).
 * Use this for events that don't trigger navigation.
 * 
 * @param name - Event name
 * @param properties - Event properties
 */
export function trackEvent(
  name: string,
  properties?: Record<string, unknown>
): void {
  try {
    track(name, properties as Record<string, string | number | boolean | null>);
    
    // Log in development to validate events are being sent
    if (process.env.NODE_ENV === "development") {
      Logger.debug(`[Analytics] Tracked event: ${name}`, properties || {});
    }
    
    // Store for beforeunload flushing
    pendingEvents.push({ name, properties });
  } catch (error) {
    // Don't block user flow if tracking fails
    Logger.error(`[Analytics] Failed to track event: ${name}`, error);
  }
}

/**
 * Flush pending events using sendBeacon (for beforeunload handler).
 * This is a safety net to ensure events are sent even during page unload.
 */
function flushPendingEvents(): void {
  if (pendingEvents.length === 0) {
    return;
  }

  // Log in development to see if flush is being called
  if (process.env.NODE_ENV === "development") {
    Logger.debug(`[Analytics] Flushing ${pendingEvents.length} pending event(s)`, {
      events: pendingEvents.map(e => e.name),
    });
  }

  // Try to send pending events via sendBeacon
  // Note: This requires knowing Vercel's endpoint, which may not be publicly available
  // For now, we'll rely on the delay mechanism and Vercel's built-in queue
  // This function is here as a placeholder for future enhancement
  
  // Clear pending events after attempting to flush
  pendingEvents.length = 0;
}

/**
 * Initialize beforeunload handler to flush events.
 * Should be called once in the root layout.
 */
export function initAnalyticsFlush(): void {
  if (typeof window === "undefined") return;

  // Flush events on page unload
  window.addEventListener("beforeunload", () => {
    flushPendingEvents();
  });

  // Also flush when page becomes hidden (mobile browsers, tab switching)
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      flushPendingEvents();
    }
  });
}

